import { useMemo, useRef, useState, type KeyboardEvent } from "react";

import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useLanguage } from "../../i18n/language-provider";
import { DEMAND_LEVELS_ORDERED, DEMAND_TINT_SOFT_CLASS } from "../calendarMetricScale";
import { PREVIEW_ROOMS, previewNights, quotePreviewNight } from "./pricePreviewModel";
import { adjustmentTone, capitalise, PREVIEW_CURRENCY as CURRENCY, withTrueMinus } from "./previewFormat";

/** What a phone shows: Thursday to Sunday, the weekend the week leads with. */
const PHONE_NIGHTS = 4;

/**
 * Tailwind's `sm:`. The welcome page has no navigation rail, so the preview's
 * width follows the window and a viewport query is the honest reading of it.
 */
const WIDE_QUERY = "(min-width: 640px)";

const ARROW_MOVES: Record<string, [number, number]> = {
    ArrowUp: [-1, 0],
    ArrowDown: [1, 0],
    ArrowLeft: [0, -1],
    ArrowRight: [0, 1],
};

interface PricePreviewProps {
    /** The day the example week is counted from; today unless a test pins it. */
    today?: Date;
}

/**
 * The welcome page's product preview: an example week of the Prices grid,
 * tinted by demand exactly as the calendar tints it, with the selected night's
 * price explained line by line underneath — the promise the product makes
 * about every price it sends, shown rather than claimed. Labelled as an
 * example throughout, because the numbers are a worked example
 * (`pricePreviewModel.ts`), not a property's.
 *
 * The week is counted from the day the page is opened, so it is always the
 * one ahead. The explanation names the weekday, the demand, the weather and
 * the competitors, and closes on the price Optivo arrives at — named as that,
 * since a visitor may not know what a PMS is.
 */
export function PricePreview({ today }: PricePreviewProps) {
    const { t, locale } = useLanguage();
    const wide = useMediaQuery(WIDE_QUERY);
    const [origin] = useState(() => today ?? new Date());
    const nights = useMemo(() => previewNights(origin), [origin]);
    const shown = wide ? nights : nights.slice(0, PHONE_NIGHTS);
    const [selection, setSelection] = useState(() => ({
        room: PREVIEW_ROOMS.findIndex((room) => room.key === "family"),
        night: nights.findIndex((night) => night.date.getDay() === 6),
    }));
    const gridRef = useRef<HTMLTableElement>(null);

    const formats = useMemo(() => {
        const money = { style: "currency", currency: CURRENCY, maximumFractionDigits: 0 } as const;
        return {
            money: new Intl.NumberFormat(locale, money),
            signedMoney: new Intl.NumberFormat(locale, { ...money, signDisplay: "exceptZero" }),
            bare: new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
            signedPct: new Intl.NumberFormat(locale, {
                style: "percent",
                maximumFractionDigits: 0,
                signDisplay: "exceptZero",
            }),
            weekdayShort: new Intl.DateTimeFormat(locale, { weekday: "short" }),
            weekdayLong: new Intl.DateTimeFormat(locale, { weekday: "long" }),
            subjectDate: new Intl.DateTimeFormat(locale, { day: "numeric", month: "numeric" }),
        };
    }, [locale]);

    const roomIndex = selection.room;
    // A phone shows fewer nights than a selection made wide may point at.
    const nightIndex = Math.min(selection.night, shown.length - 1);
    const selectedRoom = PREVIEW_ROOMS[roomIndex];
    const selectedNight = shown[nightIndex];
    const quote = quotePreviewNight(selectedRoom, selectedNight);
    const weekdayName = capitalise(formats.weekdayLong.format(selectedNight.date), locale);
    const demandWord = t.pricingCalendar.demand[selectedNight.demand].toLocaleLowerCase(locale);

    const select = (room: number, night: number) => setSelection({ room, night });

    const handleKeyDown = (event: KeyboardEvent<HTMLTableCellElement>, room: number, night: number) => {
        const move = ARROW_MOVES[event.key];
        if (!move) return;
        event.preventDefault();
        const nextRoom = Math.min(Math.max(room + move[0], 0), PREVIEW_ROOMS.length - 1);
        const nextNight = Math.min(Math.max(night + move[1], 0), shown.length - 1);
        select(nextRoom, nextNight);
        gridRef.current
            ?.querySelector<HTMLElement>(`[data-cell="${nextRoom}-${nextNight}"]`)
            ?.focus();
    };

    const signedPct = (pct: number) => withTrueMinus(formats.signedPct.format(pct / 100));
    const signedMoney = (amount: number) => withTrueMinus(formats.signedMoney.format(amount));

    return (
        <figure className="overflow-hidden rounded-lg border bg-card text-card-foreground">
            <figcaption className="flex items-start justify-between gap-3 border-b px-4 py-3 sm:px-5">
                <div className="min-w-0">
                    <p className="text-sm font-medium">{t.welcome.previewTitle}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t.welcome.previewHint}</p>
                </div>
                <span className="shrink-0 rounded-sm border border-emphasis/40 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-emphasis">
                    {t.welcome.previewExample}
                </span>
            </figcaption>

            <div className="overflow-x-auto overflow-y-hidden">
                <table
                    ref={gridRef}
                    role="grid"
                    aria-label={t.welcome.previewGridLabel.replace("{currency}", CURRENCY)}
                    className="w-full table-fixed border-collapse"
                >
                    <colgroup>
                        {/* Holds "Dvoulůžkový" whole on a phone beside four nights. */}
                        <col className="w-[6.75rem] sm:w-28" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th
                                scope="col"
                                className="px-2 py-2 text-left align-bottom text-[11px] font-medium text-muted-foreground sm:px-3"
                            >
                                {t.welcome.previewRoomColumn}
                            </th>
                            {shown.map((night) => (
                                <th
                                    key={night.date.toISOString()}
                                    scope="col"
                                    className="border-l border-border/70 px-1 py-2 text-center font-normal"
                                >
                                    <span className="block text-[11px] text-muted-foreground">
                                        {capitalise(formats.weekdayShort.format(night.date), locale)}
                                    </span>
                                    <span className="block font-mono text-sm">{night.date.getDate()}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {PREVIEW_ROOMS.map((room, r) => (
                            <tr key={room.key}>
                                <th
                                    scope="row"
                                    className="truncate border-t border-border/70 px-2 py-2.5 text-left text-sm font-medium sm:px-3"
                                >
                                    {t.welcome.previewRooms[room.key]}
                                </th>
                                {shown.map((night, n) => {
                                    const selected = r === roomIndex && n === nightIndex;
                                    return (
                                        <td
                                            key={night.date.toISOString()}
                                            role="gridcell"
                                            data-cell={`${r}-${n}`}
                                            aria-selected={selected}
                                            tabIndex={selected ? 0 : -1}
                                            onClick={() => select(r, n)}
                                            onFocus={() => select(r, n)}
                                            onKeyDown={(event) => handleKeyDown(event, r, n)}
                                            // The selected night wears the calendar's own mark: an
                                            // inset `--ring` outline (CALENDAR_HOVER_TRIGGER_CLASS).
                                            className={`${DEMAND_TINT_SOFT_CLASS[night.demand]} cursor-pointer border-l border-t border-border/70 px-1 py-2.5 text-center font-mono text-sm ${selected ? "outline-2 -outline-offset-2 outline-ring" : "hover:outline-1 hover:-outline-offset-1 hover:outline-ring/60"}`}
                                        >
                                            {formats.bare.format(quotePreviewNight(room, night).price)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-4 py-2 text-[11px] text-muted-foreground sm:px-5">
                <span>{t.pricingCalendar.demand.legendLabel}</span>
                {DEMAND_LEVELS_ORDERED.map((level) => (
                    <span key={level} className="inline-flex items-center gap-1">
                        <span
                            aria-hidden="true"
                            className={`size-2.5 rounded-sm border border-border/70 ${DEMAND_TINT_SOFT_CLASS[level]}`}
                        />
                        {t.pricingCalendar.demand[level]}
                    </span>
                ))}
            </div>

            <div
                data-testid="price-preview-explanation"
                aria-live="polite"
                className="border-t bg-background/40 px-4 py-4 sm:px-5"
            >
                <p className="text-xs text-muted-foreground">
                    {weekdayName} {formats.subjectDate.format(selectedNight.date)} ·{" "}
                    {t.welcome.previewRooms[selectedRoom.key]}
                </p>
                <p className="type-display-section mt-1 text-2xl">
                    {t.welcome.previewWhy.replace("{price}", formats.money.format(quote.price))}
                </p>
                <dl className="mt-3 text-sm">
                    <ExplanationLine label={t.welcome.previewBase} amount={formats.money.format(quote.base)} />
                    <ExplanationLine
                        label={weekdayName}
                        pct={signedPct(quote.weekdayPct)}
                        amount={signedMoney(quote.weekdayAmount)}
                        tone={adjustmentTone(quote.weekdayAmount)}
                    />
                    <ExplanationLine
                        label={t.welcome.previewDemand.replace("{level}", demandWord)}
                        pct={signedPct(quote.demandPct)}
                        amount={signedMoney(quote.demandAmount)}
                        tone={adjustmentTone(quote.demandAmount)}
                    />
                    {/* Demand is not all that is read: the forecast and the
                        competitors' prices each get a line, on every night,
                        so one that leaves the price alone says so with a 0. */}
                    <ExplanationLine
                        label={t.welcome.previewWeather[selectedNight.weather]}
                        pct={signedPct(quote.weatherPct)}
                        amount={signedMoney(quote.weatherAmount)}
                        tone={adjustmentTone(quote.weatherAmount)}
                    />
                    <ExplanationLine
                        label={t.welcome.previewCompetitors[selectedNight.competitors]}
                        pct={signedPct(quote.competitorPct)}
                        amount={signedMoney(quote.competitorAmount)}
                        tone={adjustmentTone(quote.competitorAmount)}
                    />
                    <ExplanationLine
                        label={t.welcome.previewFinal}
                        amount={formats.money.format(quote.price)}
                        total
                    />
                </dl>
            </div>
        </figure>
    );
}

interface ExplanationLineProps {
    label: string;
    amount: string;
    pct?: string;
    tone?: string;
    total?: boolean;
}

function ExplanationLine({ label, amount, pct, tone = "", total = false }: ExplanationLineProps) {
    return (
        <div
            className={`grid grid-cols-[minmax(0,1fr)_auto_minmax(5.5rem,auto)] items-baseline gap-3 py-1 ${total ? "mt-1 border-t border-foreground/25 pt-2 font-medium" : ""}`}
        >
            <dt className="min-w-0">{label}</dt>
            <dd className="font-mono text-xs text-muted-foreground">{pct ?? ""}</dd>
            <dd className={`text-right font-mono ${tone}`}>{amount}</dd>
        </div>
    );
}
